import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { upgradeApi, type UpgradeStatus } from '../api/client'

const key = (id: string) => ['instances', id, 'upgrade']

/** Stages where something runs in the background and the panel should poll. */
const BUSY_STAGES = new Set([
  'cloning', 'pulling_code', 'migrating', 'baseline', 'backup', 'copying_db',
  'copying_filestore', 'staging_code', 'switching', 'switched', 'health',
])

export function isUpgradeBusy(s: UpgradeStatus | undefined): boolean {
  const u = s?.upgrade
  if (!u || u.error) return false
  return u.state === 'production' ? u.stage !== 'awaiting_dump' : BUSY_STAGES.has(u.stage)
}

export function useUpgradeStatus(instanceId: string) {
  return useQuery({
    queryKey: key(instanceId),
    queryFn: () => upgradeApi.status(instanceId),
    refetchInterval: (q) => (isUpgradeBusy(q.state.data) ? 3000 : false),
  })
}

export function useUpgradePrecheck(instanceId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...key(instanceId), 'precheck'],
    queryFn: () => upgradeApi.precheck(instanceId),
    enabled,
  })
}

/** Every action invalidates the status and the instance it changes. */
export function useUpgradeAction<TArgs = void>(instanceId: string, fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key(instanceId) })
      qc.invalidateQueries({ queryKey: ['instances', instanceId] })
    },
  })
}
